import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  ArrowLeft, Plus, Trash2, Save, X, Puzzle,
  ChevronDown, ChevronUp
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminMemory = () => {
  const navigate = useNavigate();
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedMemory, setExpandedMemory] = useState(null);
  const [filter, setFilter] = useState({ age_group: '', subject: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMemory, setNewMemory] = useState({
    age_group: 'preschool',
    subject: '',
    round_number: 1,
    cards: [{ value: '', display: '' }]
  });

  useEffect(() => {
    if (!localStorage.getItem('adminAuth')) {
      navigate('/admin');
      return;
    }
    fetchMemories();
  }, [navigate]);

  const fetchMemories = async () => {
    try {
      const response = await axios.get(`${API}/admin/memory`);
      setMemories(response.data.memories);
    } catch (err) {
      console.error('Error fetching memories:', err);
    }
    setLoading(false);
  };

  const handleDelete = async (memoryId) => {
    if (!window.confirm('Are you sure you want to delete this memory game?')) return;
    try {
      await axios.delete(`${API}/admin/memory/${memoryId}`);
      fetchMemories();
    } catch (err) {
      alert('Error deleting memory game');
    }
  };

  const handleAddMemory = async () => {
    try {
      await axios.post(`${API}/admin/memory`, newMemory);
      setShowAddForm(false);
      setNewMemory({
        age_group: 'preschool',
        subject: '',
        round_number: 1,
        cards: [{ value: '', display: '' }]
      });
      fetchMemories();
    } catch (err) {
      alert('Error creating memory game');
    }
  };

  const addCard = () => {
    setNewMemory({
      ...newMemory,
      cards: [...newMemory.cards, { value: '', display: '' }]
    });
  };

  const updateCard = (index, field, value) => {
    const updated = [...newMemory.cards];
    updated[index][field] = value;
    setNewMemory({ ...newMemory, cards: updated });
  };

  const filteredMemories = memories.filter(m => {
    if (filter.age_group && m.age_group !== filter.age_group) return false;
    if (filter.subject && !m.subject.includes(filter.subject)) return false;
    return true;
  });

  const groupedMemories = filteredMemories.reduce((acc, memory) => {
    const key = `${memory.age_group}-${memory.subject}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(memory);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="text-white hover:bg-white/20">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <Puzzle className="w-8 h-8" />
              <div>
                <h1 className="text-2xl font-bold">Memory Games</h1>
                <p className="text-blue-100">{memories.length} memory rounds</p>
              </div>
            </div>
            <Button onClick={() => setShowAddForm(true)} className="bg-white text-blue-600 hover:bg-blue-50">
              <Plus className="w-4 h-4 mr-2" /> Add Memory Game
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <select
            value={filter.age_group}
            onChange={(e) => setFilter({ ...filter, age_group: e.target.value })}
            className="px-4 py-2 rounded-lg border border-gray-300"
          >
            <option value="">All Age Groups</option>
            <option value="preschool">Preschool</option>
            <option value="elementary">Elementary</option>
            <option value="advanced">Advanced</option>
          </select>
          <Input
            placeholder="Search subject..."
            value={filter.subject}
            onChange={(e) => setFilter({ ...filter, subject: e.target.value })}
            className="max-w-xs"
          />
        </div>

        {/* Memory List */}
        <div className="space-y-4">
          {Object.entries(groupedMemories).map(([key, rounds]) => (
            <Card key={key} className="p-4 bg-white shadow" style={{ borderRadius: '12px' }}>
              <div 
                className="flex justify-between items-center cursor-pointer"
                onClick={() => setExpandedMemory(expandedMemory === key ? null : key)}
              >
                <div>
                  <span className="px-2 py-1 rounded text-xs font-semibold bg-blue-100 text-blue-700 mr-2">
                    {rounds[0].age_group}
                  </span>
                  <span className="font-bold text-gray-800 capitalize">{rounds[0].subject}</span>
                  <span className="text-gray-500 ml-2">({rounds.length} rounds)</span>
                </div>
                {expandedMemory === key ? <ChevronUp /> : <ChevronDown />}
              </div>
              
              {expandedMemory === key && (
                <div className="mt-4 space-y-3">
                  {rounds.sort((a, b) => a.round_number - b.round_number).map((memory) => (
                    <div key={memory.memory_id} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold">Round {memory.round_number}</span>
                        <Button size="sm" variant="outline" onClick={() => handleDelete(memory.memory_id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{memory.cards?.length || 0} cards</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {memory.cards?.map((card, i) => (
                          <span key={i} className="px-2 py-1 bg-white rounded border text-sm">
                            {card.display}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>

        {/* Add Memory Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 bg-white" style={{ borderRadius: '16px' }}>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">Add New Memory Game</h2>
                <Button variant="ghost" onClick={() => setShowAddForm(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <select
                    value={newMemory.age_group}
                    onChange={(e) => setNewMemory({ ...newMemory, age_group: e.target.value })}
                    className="px-3 py-2 rounded-lg border"
                  >
                    <option value="preschool">Preschool</option>
                    <option value="elementary">Elementary</option>
                    <option value="advanced">Advanced</option>
                  </select>
                  <Input
                    placeholder="Subject (e.g., abc)"
                    value={newMemory.subject}
                    onChange={(e) => setNewMemory({ ...newMemory, subject: e.target.value })}
                  />
                  <Input
                    type="number"
                    placeholder="Round #"
                    value={newMemory.round_number}
                    onChange={(e) => setNewMemory({ ...newMemory, round_number: parseInt(e.target.value) })}
                  />
                </div>

                <div className="space-y-3">
                  <p className="text-sm text-gray-600">Cards (pairs will be created automatically):</p>
                  {newMemory.cards.map((card, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        placeholder="Value (e.g., A)"
                        value={card.value}
                        onChange={(e) => updateCard(index, 'value', e.target.value)}
                      />
                      <Input
                        placeholder="Display (e.g., 🅰️ or A)"
                        value={card.display}
                        onChange={(e) => updateCard(index, 'display', e.target.value)}
                      />
                    </div>
                  ))}
                </div>

                <Button onClick={addCard} variant="outline" className="w-full">
                  <Plus className="w-4 h-4 mr-2" /> Add Card
                </Button>

                <Button onClick={handleAddMemory} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                  <Save className="w-4 h-4 mr-2" /> Save Memory Game
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMemory;
